'use client';

import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import LoadingIndicator from '@storeblocks/loading-indicator';

import useTranslation from '../../translation/useTranslation';
import Toggle, { ToggleProps } from '../Toggle';
import { useCart, useNotifications } from './state';
import { useToggles } from '../state';

import Empty from './Empty';
import Fault from './Fault';
import Cart from './Cart';
import Entry from './Entry';

type NotificationsProps = Partial<ToggleProps>;

function Notifications({
  id = 'notifications',
  trackid = 'menu-1_expand|Varslinger',
  title = '',
  icon = <i aria-hidden>alert</i>,
  children,
  ...props
}: NotificationsProps) {
  const { t } = useTranslation();
  const { openId } = useToggles();
  const { cart, fetchCart } = useCart();
  const { notifications, fetchNotifications, fetchUnreadCount } = useNotifications();

  const cartPresent = cart.id && cart.count;
  const eventsPresent = !!notifications.data.length;

  const open = openId === id;

  useEffect(() => {
    fetchCart();
    fetchUnreadCount();
  }, [fetchCart, fetchUnreadCount]);

  useEffect(() => {
    if (open && !eventsPresent) {
      fetchNotifications();
    }
  }, [open, eventsPresent, fetchNotifications]);

  const notificationItems = notifications
    .data.map((notification) => <Entry key={notification.id} notification={notification} />);

  const content = useMemo(() => {
    if (notifications.error) {
      return <Fault />;
    }

    if (notifications.loading) {
      return <LoadingIndicator color="background" />;
    }

    if (!cartPresent && !eventsPresent) {
      return <Empty />;
    }

    return (
      <>
        <Cart />
        {notificationItems}
      </>
    );
  }, [notifications, cartPresent, eventsPresent, notificationItems]);

  return (
    <Toggle
      id={id}
      trackid={trackid}
      title={title || t('notification.text')}
      hideTextOnMobile={false}
      icon={icon}
      {...props}
    >
      <Grid>{children || content}</Grid>
    </Toggle>
  );
}

Notifications.Entry = Entry;
Notifications.Cart = Cart;
Notifications.Empty = Empty;

export default Notifications;

export const Grid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
`;
